/**
 * web/src/hooks/mutations/useSetGmailEventDefault.ts
 *
 * Multi-account Gmail (2026-06) — PUT /api/me/gmail/event-default.
 * Sets (or clears, with connectionId: null) the caller's default sending
 * connection for one event. The send-time fallback order is:
 * per-send override → this per-event default → first-connected account.
 *
 * Cache invalidation scope: ONLY qk.me() — the mapping lives entirely on the
 * Me payload (gmailEventDefaults) and changes no workflow state.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../lib/queryKeys';
import { toast } from 'sonner';
import { api, toastApiError } from '../../lib/api';

/** Body for the mutation. `connectionId: null` clears the mapping. */
export interface SetGmailEventDefaultBody {
  eventId: string;
  connectionId: string | null;
}

/**
 * React Query mutation hook for the per-event default sending account.
 *
 * @returns React Query mutation handle. Caller invokes
 *          `mutate({ eventId, connectionId })`.
 * @sideEffects HTTP PUT /api/me/gmail/event-default; invalidates qk.me() and
 *              toasts on settle.
 * @idempotent yes — same body lands the mapping at the same final state.
 */
export function useSetGmailEventDefault() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetGmailEventDefaultBody) =>
      api<{ ok: boolean }>('/api/me/gmail/event-default', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: (_res, body) => {
      qc.invalidateQueries({ queryKey: qk.me() });
      toast.success(
        body.connectionId === null
          ? 'Default cleared — sends use your first-connected account.'
          : 'Default sending account saved.',
      );
    },
    onError: (err) => {
      toastApiError(err, "Couldn't save the default sending account — try again.");
    },
  });
}
